import { useRef, useState, useEffect } from "react";
import { Play, Pause, Download } from "lucide-react";

interface TrackRowProps {
  label: string;
  url: string;
  trackId: string;
  activeTrackId: string | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  onTogglePlay: (url: string, trackId: string) => void;
  onSeek: (pct: number) => void;
  showDownload?: boolean;
}

const formatTime = (s: number) => {
  if (!s || !isFinite(s)) return "0:00";
  const mins = Math.floor(s / 60);
  const secs = Math.floor(s % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const TrackRow = ({
  label,
  url,
  trackId,
  activeTrackId,
  isPlaying,
  progress,
  duration,
  onTogglePlay,
  onSeek,
  showDownload = false,
}: TrackRowProps) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isActive = activeTrackId === trackId;
  const isCurrentlyPlaying = isActive && isPlaying;

  const calcPct = (clientX: number) => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive) return;
    setIsDragging(true);
    onSeek(calcPct(e.clientX));
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => onSeek(calcPct(e.clientX));
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, onSeek]);

  const pct = isActive && duration > 0 ? (progress / duration) * 100 : 0;
  const elapsed = isActive ? formatTime(progress) : "0:00";
  const remaining = isActive && duration > 0 ? `-${formatTime(duration - progress)}` : "—:——";

  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onTogglePlay(url, trackId)}
          className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors shrink-0"
        >
          {isCurrentlyPlaying ? (
            <Pause className="w-4 h-4 text-primary" />
          ) : (
            <Play className="w-4 h-4 text-primary ml-0.5" />
          )}
        </button>
        <span className="text-sm text-foreground flex-1 min-w-0 truncate">{label}</span>
        {showDownload && (
          <a href={url} download className="text-primary hover:text-primary/80 transition-colors shrink-0">
            <Download className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-2 px-11">
        <div
          ref={barRef}
          className={`w-full h-2 bg-muted rounded-full relative ${isActive ? "cursor-pointer" : "cursor-default"}`}
          onMouseDown={handleMouseDown}
          onTouchStart={(e) => { if (isActive) onSeek(calcPct(e.touches[0].clientX)); }}
          onTouchMove={(e) => { if (isActive) onSeek(calcPct(e.touches[0].clientX)); }}
        >
          <div
            className="h-full bg-primary rounded-full transition-[width] duration-100"
            style={{ width: `${pct}%` }}
          />
          {isActive && duration > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary border-2 border-background shadow-sm transition-[left] duration-100"
              style={{ left: `calc(${pct}% - 6px)` }}
            />
          )}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">{elapsed}</span>
          <span className="text-[10px] text-muted-foreground">{remaining}</span>
        </div>
      </div>
    </div>
  );
};

export default TrackRow;
